import React from 'react';
import { useTenant } from '../contexts/TenantContext';
import { Lock } from 'lucide-react';

const PermissionGuard = ({ 
  children, 
  requiredPermission, 
  fallback = null,
  showAccessDenied = true 
}) => {
  const { hasPermission, currentTenant } = useTenant();

  // If no tenant is selected, show loading
  if (!currentTenant) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Check if user has required permission
  if (hasPermission(requiredPermission)) {
    return children;
  }

  // Show custom fallback if provided
  if (fallback) {
    return fallback;
  }

  // Show access denied message
  if (showAccessDenied) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <div className="w-16 h-16 bg-red-900 bg-opacity-20 rounded-full flex items-center justify-center mb-4">
          <Lock className="w-8 h-8 text-red-400" />
        </div>
        <h3 className="text-lg font-semibold text-white mb-2">
          Access Denied
        </h3>
        <p className="text-gray-400 mb-4 max-w-md">
          You don't have permission to access this feature. 
          Required permission: <span className="font-mono text-blue-400">{requiredPermission}</span>
        </p>
        <div className="text-sm text-gray-500">
          Contact your administrator to request access.
        </div>
      </div>
    );
  }

  // Return nothing if no fallback and access denied is hidden
  return null;
};

// Specialized permission guards for common use cases
export const AdminOnly = ({ children, fallback }) => (
  <PermissionGuard requiredPermission="admin" fallback={fallback}>
    {children}
  </PermissionGuard>
);

export const WriteAccess = ({ children, fallback }) => (
  <PermissionGuard requiredPermission="write" fallback={fallback}>
    {children}
  </PermissionGuard>
);

export const ReadAccess = ({ children, fallback }) => (
  <PermissionGuard requiredPermission="read" fallback={fallback}>
    {children}
  </PermissionGuard>
);

// Conditional rendering based on permissions
export const ConditionalRender = ({ 
  children, 
  requiredPermission, 
  fallback = null 
}) => {
  const { hasPermission } = useTenant();
  
  if (hasPermission(requiredPermission)) {
    return children;
  }
  
  return fallback;
};

// Feature flag component
export const FeatureFlag = ({ 
  feature, 
  children, 
  fallback = null,
  enabled = true 
}) => {
  const { currentTenant } = useTenant();
  
  if (!enabled) {
    return fallback;
  }
  
  // Check if tenant has access to this feature
  const hasFeatureAccess = currentTenant?.features?.includes(feature);
  
  if (hasFeatureAccess) {
    return children;
  }
  
  return fallback;
};

export default PermissionGuard;
